import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Field, FormSpy } from 'react-final-form';
import BackArrow from 'assets/back-arrow.svg';
import Copy from 'assets/smallcopy.svg';
import ThemeStore from 'stories/ThemeStore';
import { Card } from '../card';
import Music from '../../../../../assets/musical.svg';
import { useSmartLink } from '../../../../../hooks/useSmartLink';
import { useNavigate, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useRelease } from 'components/NewRelease/hooks/useRelease';
import { ProgressSpinner } from 'primereact/progressspinner';

interface CreationProps {
  text?: string;
  values: any;
  setValues: (values: any) => void;
}

export const Creation: React.FC<CreationProps> = ({ text = '', values, setValues }) => {
  const [name, setName] = useState('');
  const [initialValues, setInitialValues] = useState<any>({});
  const theme = ThemeStore.activeTheme;
  const { loading, error, data: allData, createSmartLink, updateSmartLink } = useSmartLink();
  const { release, fetching, updateRelease, fetchRelease } = useRelease();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastSegment = location.pathname.split('/').pop();
  const id = params.id;
  const data = lastSegment === 'edit' && allData?.filter((value: any) => value.id == id);
  const releaseData = data?.[0];

  useEffect(() => {
    fetchRelease()
  }, [])

  useEffect(() => {
    if (releaseData && lastSegment === 'edit') {
      setName(releaseData?.release?.title || '');
      setInitialValues({
        sort_platform_links_by_click: releaseData.sort_platform_links_by_click,
        customLink: releaseData.share_url || '',
        platform_links: releaseData.platform_links?.map((link: { url: string }) => link.url) || [],
        social_links: releaseData.social_links?.map((link: { url: string }) => link.url) || [],
        youtube_links: releaseData.youtube_links?.map((link: { url: string }) => link.url) || [],
        description: releaseData.description || '',
      });
    }
  }, [releaseData]);

  useEffect(() => {
    if (release && lastSegment === 'new') {
      setName(release?.title || '');
      setInitialValues({
        sort_platform_links_by_click: false,
        customLink: '',
        platform_links: [],
        social_links: [],
        youtube_links: [],
        description: '',
      });
    }
  }, [release]);

  const onSubmit = async (values: any) => {
    if (lastSegment === 'edit') {
      await updateSmartLink(String(id), {
        sort_platform_links_by_click: values.sort_platform_links_by_click,
        release_id: id,
        customLink: values.customLink,
        description: values.description,
        platform_links: values.platform_links.map((link: string) => ({ url: link })),
        social_links: values.social_links.map((link: string) => ({ url: link })),
        youtube_links: values.youtube_links.map((link: string) => ({ url: link })),
      });
    } else {
      await createSmartLink({
        sort_platform_links_by_click: values.sort_platform_links_by_click,
        release_id: id,
        customLink: values.customLink,
        description: values.description,
        platform_links: values.platform_links.map((link: string) => ({ url: link })),
        social_links: values.social_links.map((link: string) => ({ url: link })),
        youtube_links: values.youtube_links.map((link: string) => ({ url: link })),
      });
    }
    navigate('/smart-links');
  };

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  if (!releaseData && !release) return <Loading>
    <ProgressSpinner />
  </Loading>



  return (
    <Content>
      <Form
        key={JSON.stringify(initialValues)}
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values: newValues }) => {
                setValues((prev: any) => {
                  if (JSON.stringify(prev) !== JSON.stringify(newValues)) {
                    return newValues;
                  }
                  return prev;
                });
              }}
            />

            <ContentBigBox>
              <TitleBox>
                <Title onClick={() => window.history.back()}>
                  <span>Release search</span>
                  <BackArrow color={theme.palette.text.breadcrumbs} />
                  <span>{name}</span>
                </Title>

                <Box>
                  <ImageWrapper>
                    {release?.cover_url && <ImagBox src={release?.cover_url} alt="Vinilovi" />}
                    <Tooltip>
                      Link appearance lets you customize your page URL:
                      choose a short, memorable name, set a domain,
                      and control how the link looks when shared.
                    </Tooltip>
                  </ImageWrapper>

                  <ContentBox>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginBottom: '7px',
                      }}
                    >
                      <Text>{name}</Text>
                      <TextLink>Link appearance</TextLink>
                    </div>

                    <InputBox>
                      <Field name="customLink">
                        {({ input }) => (
                          <>
                            {input.value && <Music />}
                            <Input {...input} placeholder="slashmusic.net/" />
                          </>
                        )}
                      </Field>
                      <CopyIcon onClick={() => handleCopy(values.customLink || '')}>
                        <Copy />
                      </CopyIcon>
                    </InputBox>
                  </ContentBox>
                </Box>
              </TitleBox>

              <Field name="platform_links">
                {({ input }) => (
                  <Card
                    title="Platform link"
                    toggle={true}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                  />
                )}
              </Field>

              <Field name="social_links">
                {({ input }) => (
                  <Card
                    title="Social network link"
                    toggle={false}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                  />
                )}
              </Field>

              <Field name="youtube_links">
                {({ input }) => (
                  <Card
                    title="Youtube link"
                    toggle={false}
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                  />
                )}
              </Field>

              <Field name="description">
                {({ input }) => (
                  <DescriptionBox>
                    <span>Release description</span>
                    <Description {...input} placeholder="Enter the release description" />
                  </DescriptionBox>
                )}
              </Field>

              <Footer>
                <SubmitButton
                  type="submit"
                  disabled={!values.customLink || values.customLink.trim() === ''}
                >
                  Create a smart link
                </SubmitButton>
              </Footer>
            </ContentBigBox>
          </form>
        )}
      />
    </Content>
  );
};

const Content = styled.div`
  width: 60%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ContentBigBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TitleBox = styled.div`
  width: 100%;
  height: 195px;
  border-radius: 25px;
  background: #FFFFFF;
  padding: 29px 24px 29px 19px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
`;

const Box = styled.div`
  display: flex;
  gap: 14px;
`;

const ImagBox = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 10px;
  display: block;
  cursor: pointer;
`;

const ContentBox = styled.div`
  width: 100%;
`;

const Text = styled.div`
  font-family: SF Pro Display;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
`;

const TextLink = styled.div`
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
`;

const InputBox = styled.div`
  width: 100%;
  display: flex;
  gap: 6px;
  align-items: center;
  position: relative;
  border-radius: 8px;
  border: 1px solid #E9E9E9;
  padding: 7px 30px 6px 14px;
`;

const Input = styled.input`
  width: 90%;
  height: 25px;
  border: none;
  outline: none;
`;

const CopyIcon = styled.div`
  width: 14px;
  height: 14px;
  position: absolute;
  top: 10px;
  right: 11px;
  cursor: pointer;
`;

const DescriptionBox = styled.div`
  width: 100%;
  height: 207px;
  border-radius: 25px;
  background: rgba(255, 255, 255, 1);
  padding:21px 17px 27px 17px;
  display: flex;
  flex-direction: column;
  gap:13px;

  span{
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
    color: rgba(0, 0, 0, 1);
  }
`;

const Description = styled.textarea`
  width: 100%;
  height: 119px;
  border: 1px solid rgba(233, 233, 233, 1);
  border-radius: 8px;
  outline: none;
  padding: 11px 14px;
  resize: none;
  ::placeholder {
    font-family: SF Pro Display;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color:rgba(0, 0, 0, 0.5);
  }
`;

const Footer = styled.div`
  width: 100%;
  height: 79px;
  border-radius: 25px;
  background: rgba(255, 255, 255, 1);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 17px 0 0;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  width: 209px;
  height: 47px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ disabled }) => (disabled ? 'rgba(103, 119, 255, 0.5)' : 'rgba(103, 119, 255, 1)')};
  color: #fff;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const ImageWrapper = styled.div`
  display: inline-block;
  position: relative;
  &:hover > div {
    opacity: 1;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 15%;
  left: 32%;
  transform: translateX(-50%);
  background: rgba(226, 229, 255, 1);
  opacity: 0;  
  pointer-events: none;
  transition: opacity 0.2s;
  width: 241px;
  height: 131px;
  border-radius: 15px;
  padding: 18px 6px 13px 9px;
  box-shadow: 0px 0px 10px rgba(145, 145, 145, 0.47);
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  z-index: 9999999;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;
